type JsonRecord = Record<string, unknown>;

type PricingColumnRole =
  | 'item'
  | 'text'
  | 'unit_price'
  | 'quantity'
  | 'discount'
  | 'tax'
  | 'fee'
  | 'line_total';

type RequiredRole = 'item' | 'unit_price' | 'quantity' | 'line_total';

export type SemanticPricingTableLabels = Partial<
  Record<'item' | 'unitPrice' | 'quantity' | 'lineTotal', string>
>;

const REQUIRED_ROLES: readonly RequiredRole[] = ['item', 'unit_price', 'quantity', 'line_total'];
const ADJUSTMENT_ROLES: readonly PricingColumnRole[] = ['discount', 'tax', 'fee'];
const COLUMN_ROLES: readonly PricingColumnRole[] = [
  ...REQUIRED_ROLES,
  'text',
  ...ADJUSTMENT_ROLES,
];

const DEFAULT_LABELS = {
  item: 'Item',
  unitPrice: 'Unit price',
  quantity: 'Quantity',
  lineTotal: 'Total',
} as const;

type ColumnEntry = {
  column: JsonRecord;
  cells: string[];
};

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isRequiredRole = (role: PricingColumnRole): role is RequiredRole =>
  (REQUIRED_ROLES as readonly string[]).includes(role);

const asCellText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
};

const parseAmount = (value: unknown): number => {
  const text = asCellText(value).replace(/,/g, '').replace(/[^\d.-]/g, '');
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const normalizeRole = (value: unknown): PricingColumnRole =>
  typeof value === 'string' && (COLUMN_ROLES as readonly string[]).includes(value)
    ? (value as PricingColumnRole)
    : 'text';

const normalizeFormat = (role: PricingColumnRole, value: unknown) => {
  if (role === 'item' || role === 'text' || role === 'quantity') return null;
  if (role === 'unit_price' || role === 'line_total') return 'currency' as const;
  return value === 'currency' ? ('currency' as const) : ('percentage' as const);
};

const defaultCellForRole = (role: PricingColumnRole): string => {
  if (role === 'quantity') return '1';
  if (role === 'unit_price' || role === 'line_total') return '0';
  return '';
};

const makeDefaultColumn = (
  role: RequiredRole,
  labels: SemanticPricingTableLabels,
): JsonRecord => ({
  label:
    role === 'item'
      ? labels.item || DEFAULT_LABELS.item
      : role === 'unit_price'
        ? labels.unitPrice || DEFAULT_LABELS.unitPrice
        : role === 'quantity'
          ? labels.quantity || DEFAULT_LABELS.quantity
          : labels.lineTotal || DEFAULT_LABELS.lineTotal,
  role,
  format: normalizeFormat(role, null),
});

const normalizeColumnWidths = (widths: unknown[], columnCount: number): number[] | null => {
  if (widths.length !== columnCount) return null;
  if (
    !widths.every(
      (width): width is number =>
        typeof width === 'number' && Number.isFinite(width) && width > 0,
    )
  ) {
    return null;
  }

  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= 0) return null;

  const normalized = widths.map((width) => Math.round((width / total) * 10000) / 100);
  const normalizedTotal = normalized.reduce((sum, width) => sum + width, 0);
  const diff = Math.round((100 - normalizedTotal) * 100) / 100;
  const largestIndex = normalized.reduce(
    (maxIndex, width, index, source) => (width > (source[maxIndex] ?? 0) ? index : maxIndex),
    0,
  );
  const largestWidth = normalized[largestIndex];
  if (largestWidth !== undefined) {
    normalized[largestIndex] = Math.round((largestWidth + diff) * 100) / 100;
  }
  return normalized;
};


/**
 * Repairs a pricing table so it satisfies the semantic pricing contract without discarding
 * user data. Existing columns, labels, formats, and cells are preserved. Missing required
 * columns are added, duplicate required roles are demoted to text, and `line_total` is moved
 * last. A table with no rows gets one empty row so it stays a valid block.
 *
 * This is intentionally tolerant: it is used at the agent boundary (builder -> simple JSON and
 * project-agent validation), where documents written before the required-role rule must keep
 * working. It must not invent commercial values: an added unit price is derived from the row's
 * existing line total, and an added quantity is 1.
 */
export function completeSemanticPricingTable(
  input: unknown,
  labels: SemanticPricingTableLabels = {},
): unknown {
  if (!isRecord(input)) return input;

  const rawColumns = input.columns;
  const rawRows = input.rows;
  if (!Array.isArray(rawColumns) || !Array.isArray(rawRows)) return input;
  if (rawRows.some((row) => !Array.isArray(row))) return input;

  const rowsSource = rawRows as unknown[][];
  const rowCount = Math.max(rowsSource.length, 1);

  const seenRequiredRoles = new Set<RequiredRole>();
  let hasDuplicateRequiredRole = false;
  const entries: ColumnEntry[] = rawColumns.map((rawColumn, index) => {
    const column = isRecord(rawColumn) ? { ...rawColumn } : {};
    const role = normalizeRole(column.role);

    if (isRequiredRole(role)) {
      if (seenRequiredRoles.has(role)) {
        // A duplicate required role is ambiguous, not old data. Leave the table untouched so
        // validation can reject it and the model retries instead of silently changing a role.
        hasDuplicateRequiredRole = true;
      } else {
        seenRequiredRoles.add(role);
      }
    }

    column.role = role;
    column.label = typeof column.label === 'string' ? column.label : '';
    column.format = normalizeFormat(role, column.format);

    return {
      column,
      cells: rowsSource.map((row) => asCellText(row[index])),
    };
  });

  if (hasDuplicateRequiredRole) return input;

  const addedRoles = new Set<RequiredRole>();
  const hasRole = (role: RequiredRole) =>
    entries.some((entry) => entry.column.role === role);

  const lineTotalEntry = entries.find((entry) => entry.column.role === 'line_total');
  const ordered = entries.filter((entry) => entry.column.role !== 'line_total');

  if (!hasRole('item')) {
    addedRoles.add('item');
    ordered.unshift({ column: makeDefaultColumn('item', labels), cells: [] });
  }

  if (!hasRole('quantity')) {
    addedRoles.add('quantity');
    ordered.push({ column: makeDefaultColumn('quantity', labels), cells: [] });
  }

  if (!hasRole('unit_price')) {
    addedRoles.add('unit_price');
    const quantityIndex = ordered.findIndex((entry) => entry.column.role === 'quantity');
    // When quantity already exists, keep the source order and add the price just before the
    // line total. When both are new, place the price before the generated quantity column.
    const insertAt =
      addedRoles.has('quantity') && quantityIndex >= 0 ? quantityIndex : ordered.length;
    ordered.splice(insertAt, 0, {
      column: makeDefaultColumn('unit_price', labels),
      cells: [],
    });
  }

  if (lineTotalEntry) {
    ordered.push(lineTotalEntry);
  } else {
    addedRoles.add('line_total');
    ordered.push({ column: makeDefaultColumn('line_total', labels), cells: [] });
  }

  const columns = ordered.map((entry) => entry.column);
  const rows = Array.from({ length: rowCount }, (_, rowIndex) =>
    ordered.map((entry) => {
      const role = entry.column.role as PricingColumnRole;
      const value = entry.cells[rowIndex];
      return value === undefined || value === '' ? defaultCellForRole(role) : value;
    }),
  );

  const unitPriceIndex = ordered.findIndex((entry) => entry.column.role === 'unit_price');
  const quantityIndex = ordered.findIndex((entry) => entry.column.role === 'quantity');
  const lineTotalIndex = ordered.findIndex((entry) => entry.column.role === 'line_total');

  if (addedRoles.has('unit_price') && unitPriceIndex >= 0) {
    rows.forEach((row) => {
      const quantity = parseAmount(row[quantityIndex]) || 1;
      const lineTotal = parseAmount(row[lineTotalIndex]);
      row[unitPriceIndex] = String(roundCurrency(quantity > 0 ? lineTotal / quantity : 0));
    });
  }

  // An added line total stays neutral. The builder recalculates it from unit price x quantity,
  // and the generator's previous repair deliberately used 0 here instead of inventing a value.
  const repaired: JsonRecord = { ...input, columns, rows };

  if (repaired.column_widths === null) {
    delete repaired.column_widths;
  } else if (Array.isArray(repaired.column_widths)) {
    const normalizedWidths = normalizeColumnWidths(repaired.column_widths, columns.length);
    if (normalizedWidths) {
      repaired.column_widths = normalizedWidths;
    } else {
      // A zero or missing width makes a column invisible and its divider impossible to grab.
      // Automatic widths are safer than preserving an unusable array.
      delete repaired.column_widths;
    }
  }
  if (
    Array.isArray(repaired.column_cell_formats) &&
    repaired.column_cell_formats.length !== columns.length
  ) {
    delete repaired.column_cell_formats;
  }
  if (Array.isArray(repaired.row_cell_formats) && repaired.row_cell_formats.length !== rows.length) {
    delete repaired.row_cell_formats;
  }

  return repaired;
}
